// WebsitePreviewCard — renders inline in chat when Vella generates a
// website via create_landing_page / aiGenerateWebsite. Two surfaces:
//   1. A 3D-tilt preview card embedded in the chat thread (collapsed).
//   2. A full-screen expanded editor with viewport modes, refresh,
//      URL bar, fullscreen toggle, and an Edit mode that lets the user
//      click iframe elements to surface a properties panel.
//
// data shape: { html, businessName?, model?, title? }
//   html         — full HTML document string (srcDoc'd into the iframe)
//   businessName — used in the header + "yoursite.com" mock URL
//   model        — backend model id (e.g. "claude-opus-4") — not shown
//   title        — optional override for the card title
//
// Domain side-card is rendered as a sibling (DomainCard.jsx), but is
// toggled from this component so the two cards animate as a pair.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FONT, TEXT, TEXT2, TEXT3, BLUE, BLUE_DEEP, BLUE_LIGHT, BORDER, WHITE } from '../../shared';
import DomainCard from './DomainCard';
import { setPreviewExpanded } from '../../lib/previewChrome';

const VIEWPORTS = {
  desktop: { id: 'desktop', label: 'Desktop', width: '100%' },
  tablet:  { id: 'tablet',  label: 'Tablet',  width: 820 },
  phone:   { id: 'phone',   label: 'Phone',   width: 390 },
};

function siteSlug(name) {
  return (name || 'yoursite').toLowerCase().replace(/[^a-z0-9]+/g, '') || 'yoursite';
}

// Injects a tiny click-capture script into the iframe so Edit mode can
// surface the clicked element back to the parent properties panel.
function injectEditHook(html) {
  if (!html || typeof html !== 'string') return html;
  const hook = `
<script>
(function() {
  if (window.__vellaEditHookInstalled) return;
  window.__vellaEditHookInstalled = true;
  var lastHover = null;
  var lastSelected = null;
  function path(el) {
    if (!el || el.nodeType !== 1) return '';
    var parts = [];
    while (el && el.nodeType === 1 && parts.length < 6) {
      var p = el.nodeName.toLowerCase();
      if (el.id) { p += '#' + el.id; parts.unshift(p); break; }
      if (el.className && typeof el.className === 'string') {
        var c = el.className.trim().split(/\\s+/).slice(0,2).join('.');
        if (c) p += '.' + c;
      }
      parts.unshift(p);
      el = el.parentElement;
    }
    return parts.join(' > ');
  }
  function clearOutline(el, prev) {
    if (!el) return;
    if (prev && el.dataset.vellaPrevOutline !== undefined) {
      el.style.outline = el.dataset.vellaPrevOutline;
      delete el.dataset.vellaPrevOutline;
    } else {
      el.style.outline = '';
    }
  }
  window.addEventListener('message', function(e) {
    if (!e.data || e.data.source !== 'vella-parent') return;
    if (e.data.type === 'set-edit-mode') {
      window.__vellaEditMode = !!e.data.enabled;
      if (!window.__vellaEditMode) {
        clearOutline(lastHover); lastHover = null;
        clearOutline(lastSelected); lastSelected = null;
      }
    }
  });
  document.addEventListener('mouseover', function(e) {
    if (!window.__vellaEditMode) return;
    if (lastHover && lastHover !== lastSelected) clearOutline(lastHover);
    lastHover = e.target;
    if (lastHover && lastHover !== lastSelected) {
      if (lastHover.dataset.vellaPrevOutline === undefined) {
        lastHover.dataset.vellaPrevOutline = lastHover.style.outline || '';
      }
      lastHover.style.outline = '2px dashed #38BDF8';
      lastHover.style.outlineOffset = '2px';
    }
  }, true);
  document.addEventListener('click', function(e) {
    if (!window.__vellaEditMode) return;
    e.preventDefault();
    e.stopPropagation();
    if (lastSelected) clearOutline(lastSelected);
    lastSelected = e.target;
    if (lastSelected.dataset.vellaPrevOutline === undefined) {
      lastSelected.dataset.vellaPrevOutline = lastSelected.style.outline || '';
    }
    lastSelected.style.outline = '2px solid #0EA5E9';
    lastSelected.style.outlineOffset = '2px';
    var cs = window.getComputedStyle(lastSelected);
    parent.postMessage({
      source: 'vella-iframe',
      type: 'element-selected',
      tag: lastSelected.tagName.toLowerCase(),
      text: (lastSelected.innerText || '').slice(0, 240),
      path: path(lastSelected),
      color: cs.color,
      fontSize: cs.fontSize,
      fontWeight: cs.fontWeight,
      background: cs.backgroundColor,
    }, '*');
  }, true);
})();
<\/script>`;
  if (html.includes('</body>')) return html.replace('</body>', hook + '</body>');
  return html + hook;
}

export default function WebsitePreviewCard({ data, onAction }) {
  const html = (data && data.html) || '';
  const businessName = (data && (data.businessName || data.title)) || 'Your site';
  const slug = siteSlug(businessName);
  const mockUrl = `${slug}.com`;

  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [viewport, setViewport] = useState('desktop');
  const [iframeKey, setIframeKey] = useState(0);
  const [showDomain, setShowDomain] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [selected, setSelected] = useState(null);
  // D6 — hold the shimmer skeleton over the thumbnail until the frame has
  // loaded + painted one frame, then fade the styled site in. No unstyled flash.
  const [thumbLoaded, setThumbLoaded] = useState(false);
  const iframeRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => { setThumbLoaded(false); }, [html]);
  const handleThumbLoad = useCallback(() => {
    requestAnimationFrame(() => setThumbLoaded(true));
  }, []);

  const expandedHtml = editMode ? injectEditHook(html) : html;

  // Tell iframe whether edit mode is on whenever it changes.
  useEffect(() => {
    if (!expanded) return;
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(
        { source: 'vella-parent', type: 'set-edit-mode', enabled: editMode },
        '*'
      );
    } catch {}
  }, [editMode, expanded, iframeKey]);

  // Listen for element-selected messages from the iframe.
  useEffect(() => {
    function onMsg(e) {
      const d = e.data;
      if (!d || d.source !== 'vella-iframe') return;
      if (d.type === 'element-selected') {
        setSelected({
          tag: d.tag, text: d.text, path: d.path,
          color: d.color, fontSize: d.fontSize,
          fontWeight: d.fontWeight, background: d.background,
        });
      }
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Hide domain card whenever edit mode is on (per spec).
  useEffect(() => { if (editMode) setShowDomain(false); }, [editMode]);

  // Lock body scroll AND hide all Vella chrome (chat bar, orb, chips, FABs,
  // help button) while expanded, so the full-bleed preview owns the screen
  // (D10). The chat thread stays mounted underneath, so returning restores
  // the same scroll position.
  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setPreviewExpanded(true);
    return () => { document.body.style.overflow = prev; setPreviewExpanded(false); };
  }, [expanded]);

  // ESC closes expanded.
  useEffect(() => {
    if (!expanded) return;
    function onKey(e) { if (e.key === 'Escape') closeExpanded(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const onMouseMove = useCallback((e) => {
    if (expanded) return;
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top)  / r.height;
    setTilt({ x: (py - 0.5) * -8, y: (px - 0.5) * 10 });
  }, [expanded]);

  const onMouseLeave = useCallback(() => setTilt({ x: 0, y: 0 }), []);

  const openExpanded = useCallback(() => {
    setExpanded(true);
    setShowDomain(false);
  }, []);

  // Edit opens the editor PREFILLED with this site's HTML. The editor is the
  // card's own fullscreen overlay (driven by `html`/`expandedHtml`), so it is
  // always seeded with the generated source — and it lives in this widget, not
  // inside TalkToVella, so CLAUDE.md's "no editable nodes in TalkToVella" holds.
  const handleEdit = useCallback((e) => {
    e?.stopPropagation();
    setExpanded(true);
    setEditMode(true);
    setShowDomain(false);
  }, []);

  const closeExpanded = useCallback(() => {
    setExpanded(false);
    setEditMode(false);
    setSelected(null);
  }, []);

  const refresh = useCallback(() => setIframeKey((k) => k + 1), []);

  // Open the generated site in a real browser tab (blob URL).
  const openInNewTab = useCallback(() => {
    try {
      const blob = new Blob([html], { type: 'text/html' });
      const u = URL.createObjectURL(blob);
      window.open(u, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { /* popup blocked / no blob support — ignore */ }
  }, [html]);

  const toggleDomain = useCallback((e) => {
    e?.stopPropagation();
    if (expanded) return;
    setShowDomain((s) => !s);
  }, [expanded]);

  // Update style of the currently selected element in the iframe.
  const updateSelected = useCallback((patch) => {
    setSelected((cur) => cur ? { ...cur, ...patch } : cur);
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow || !iframe.contentDocument) return;
    try {
      const doc = iframe.contentDocument;
      // We re-find the last selected element by walking the path attribute
      // dataset markers (vellaPrevOutline). Simpler: query for the only
      // element that has a solid #0EA5E9 outline marker.
      const all = doc.querySelectorAll('[data-vella-prev-outline]');
      const el = Array.from(all).find((n) => /solid/.test(n.style.outline)) || all[all.length - 1];
      if (!el) return;
      if (patch.text !== undefined) el.innerText = patch.text;
      if (patch.color) el.style.color = patch.color;
      if (patch.fontSize) el.style.fontSize = patch.fontSize;
      if (patch.fontWeight) el.style.fontWeight = patch.fontWeight;
      if (patch.background) el.style.backgroundColor = patch.background;
    } catch {}
  }, []);

  if (!html) return null;

  const cardWidth = showDomain ? 'calc(100% - 320px)' : '100%';

  return (
    <>
      <style>{`
        @keyframes wpc-slideInRight { from { opacity: 0; transform: translateX(40px) rotateY(8deg); } to { opacity: 1; transform: translateX(0) rotateY(0deg); } }
        @keyframes wpc-slideOut { from { opacity: 1; } to { opacity: 0; transform: translateY(8px); } }
        @keyframes wpc-shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }
        .wpc-card { transform-style: preserve-3d; transition: transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.3s ease, width 0.4s ease; }
        .wpc-tool-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 32px; padding: 0 10px; border-radius: 9px; border: 1px solid ${BORDER}; background: rgba(255,255,255,0.85); color: ${TEXT2}; font-family: ${FONT}; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.18s ease; }
        .wpc-tool-btn:hover { border-color: ${BLUE}; color: ${BLUE_DEEP}; background: ${BLUE_LIGHT}; }
        .wpc-tool-btn.active { border-color: ${BLUE}; color: ${BLUE_DEEP}; background: ${BLUE_LIGHT}; }
        .wpc-tool-btn.primary { background: linear-gradient(135deg, #3B82F6, #2563EB); border-color: rgba(255,255,255,0.3); color: #fff; }
        .wpc-tool-btn.primary:hover { color: #fff; background: linear-gradient(135deg, #2563EB, #1D4ED8); }
        .wpc-url-bar { display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 12px; border-radius: 9px; border: 1px solid ${BORDER}; background: rgba(255,255,255,0.9); color: ${TEXT2}; font-family: ${FONT}; font-size: 12px; min-width: 140px; }
      `}</style>

      {/* Collapsed: card row that may host the sibling domain card */}
      {!expanded && (
        <div style={{
          width: '100%',
          display: 'flex',
          gap: 14,
          alignItems: 'stretch',
          fontFamily: FONT,
        }}>
          <div
            ref={cardRef}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            onClick={openExpanded}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openExpanded(); }}
            className="wpc-card"
            style={{
              flex: 1,
              minWidth: 0,
              width: cardWidth,
              maxWidth: 560,
              borderRadius: 18,
              overflow: 'hidden',
              border: `1px solid ${BORDER}`,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 100%)',
              backdropFilter: 'blur(28px) saturate(180%)',
              WebkitBackdropFilter: 'blur(28px) saturate(180%)',
              boxShadow: '0 1px 0 rgba(255,255,255,0.85) inset, 0 24px 50px -20px rgba(11,107,211,0.32), 0 10px 30px -14px rgba(14,165,233,0.20)',
              cursor: 'pointer',
              transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
              willChange: 'transform',
            }}
          >
            {/* Header — title + Connect Domain CTA */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 14px', borderBottom: `1px solid ${BORDER}`,
              background: 'rgba(255,255,255,0.55)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: 'linear-gradient(135deg, #38BDF8, #0EA5E9)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 12px -4px rgba(14,165,233,0.5)',
                  flexShrink: 0,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
                  </svg>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{businessName}</div>
                  <div style={{ fontSize: 11, color: TEXT3, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mockUrl}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={toggleDomain}
                style={{
                  flexShrink: 0,
                  fontSize: 11, fontWeight: 700, color: BLUE_DEEP,
                  padding: '6px 12px', borderRadius: 999,
                  border: `1px solid ${BORDER}`, background: WHITE,
                  cursor: 'pointer', fontFamily: FONT,
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  transition: 'all 0.18s ease',
                }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M2 12h20"/>
                </svg>
                {showDomain ? 'Hide domains' : 'Connect Domain'}
              </button>
            </div>

            {/* Thumbnail iframe */}
            <div style={{ position: 'relative', background: '#0F172A', height: 260, overflow: 'hidden' }}>
              <iframe
                title={`${businessName} preview`}
                srcDoc={html}
                sandbox=""
                scrolling="no"
                onLoad={handleThumbLoad}
                style={{
                  width: '1280px',
                  height: '800px',
                  border: 'none',
                  pointerEvents: 'none',
                  transform: 'scale(0.42)',
                  transformOrigin: 'top left',
                  background: '#fff',
                  opacity: thumbLoaded ? 1 : 0,
                  transition: 'opacity 300ms ease',
                }}
              />
              {/* D6 — shimmer skeleton until the frame has painted. */}
              {!thumbLoaded ? (
                <div aria-hidden="true" style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(100deg, #101d33 30%, #22345c 50%, #101d33 70%)',
                  backgroundSize: '200% 100%',
                  animation: 'wpc-shimmer 1.3s linear infinite',
                }} />
              ) : null}
              {/* Click-to-expand hint overlay */}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(180deg, rgba(15,23,42,0) 60%, rgba(15,23,42,0.55) 100%)',
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                padding: 12,
                pointerEvents: 'none',
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: '#fff',
                  background: 'rgba(15,23,42,0.55)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  backdropFilter: 'blur(14px)',
                  padding: '5px 12px', borderRadius: 999,
                  letterSpacing: '0.04em',
                }}>
                  Click to expand
                </div>
              </div>
            </div>

            {/* Footer — Preview + Edit actions */}
            <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openExpanded(); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 12, fontWeight: 700, color: '#fff',
                  padding: '7px 16px', borderRadius: 999, border: 'none',
                  background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                  cursor: 'pointer', fontFamily: FONT,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                </svg>
                Preview
              </button>
              <button
                type="button"
                onClick={handleEdit}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 12, fontWeight: 700, color: BLUE_DEEP,
                  padding: '7px 16px', borderRadius: 999,
                  border: `1px solid ${BORDER}`, background: WHITE,
                  cursor: 'pointer', fontFamily: FONT,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit
              </button>
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: TEXT3, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Live preview
              </span>
            </div>
          </div>

          {showDomain && (
            <div style={{ width: 320, flexShrink: 0, animation: 'wpc-slideInRight 0.45s cubic-bezier(0.2,0.8,0.2,1) both' }}>
              <DomainCard
                businessName={businessName}
                onClose={() => setShowDomain(false)}
                onAction={onAction}
              />
            </div>
          )}
        </div>
      )}

      {/* Expanded: full-bleed overlay, PORTALED to <body> so it sits above the
          whole app shell at z-index:40 (D10). All Vella chrome (chat bar, orb,
          chips, FABs, help button) is hidden via setPreviewExpanded while open,
          and the chat thread stays mounted underneath so Back/Esc restores the
          same scroll position. */}
      {expanded && createPortal((
        <div style={{
          position: 'fixed', inset: 0, zIndex: 40,
          background: 'rgba(8,16,34,0.78)',
          backdropFilter: 'blur(18px) saturate(160%)',
          WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          display: 'flex', flexDirection: 'column',
          fontFamily: FONT,
        }}>
          {/* Slim glass top bar — Back to chat · domain · Open ↗ · Edit */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 16px',
            background: 'rgba(255,255,255,0.95)',
            borderBottom: `1px solid ${BORDER}`,
            flexWrap: 'wrap',
          }}>
            {/* Back to chat + domain */}
            <button
              type="button"
              className="wpc-tool-btn"
              onClick={closeExpanded}
              title="Back to chat (Esc)"
              style={{ fontWeight: 700 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
              </svg>
              Back to chat
            </button>
            <span style={{ fontSize: 12, fontWeight: 600, color: TEXT2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{mockUrl}</span>
            <span style={{ width: 1, height: 20, background: BORDER, margin: '0 2px' }} />
            {/* Left cluster: viewport toggles + edit */}
            <div style={{ display: 'inline-flex', gap: 6 }}>
              <button
                type="button"
                className={`wpc-tool-btn ${viewport === 'desktop' ? 'active' : ''}`}
                onClick={() => setViewport('desktop')}
                title="Desktop"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </button>
              <button
                type="button"
                className={`wpc-tool-btn ${viewport === 'tablet' ? 'active' : ''}`}
                onClick={() => setViewport('tablet')}
                title="Tablet"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="16" height="20" rx="2"/>
                  <line x1="12" y1="18" x2="12" y2="18.01"/>
                </svg>
              </button>
              <button
                type="button"
                className={`wpc-tool-btn ${viewport === 'phone' ? 'active' : ''}`}
                onClick={() => setViewport('phone')}
                title="Phone"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="7" y="2" width="10" height="20" rx="2"/>
                  <line x1="12" y1="18" x2="12" y2="18.01"/>
                </svg>
              </button>
              <button
                type="button"
                className={`wpc-tool-btn ${editMode ? 'primary' : ''}`}
                onClick={() => setEditMode((v) => !v)}
                title="Edit elements"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3l2 4 4 2-4 2-2 4-2-4-4-2 4-2z"/>
                  <path d="M19 15l1 2 2 1-2 1-1 2-1-2-2-1 2-1z"/>
                </svg>
                Edit
              </button>
              <button
                type="button"
                className="wpc-tool-btn"
                onClick={() => {/* magic — placeholder */}}
                title="Magic suggestions"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9"/>
                  <path d="M8 12c0-2.2 1.8-4 4-4s4 1.8 4 4"/>
                  <circle cx="12" cy="12" r="1"/>
                </svg>
              </button>
            </div>

            {/* Middle cluster: refresh + URL + device dropdown */}
            <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <button
                type="button"
                className="wpc-tool-btn"
                onClick={refresh}
                title="Refresh"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
                </svg>
              </button>
              <div className="wpc-url-bar">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEXT3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                <span style={{ color: TEXT2, fontWeight: 600 }}>/</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={TEXT3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </div>

            {/* Right cluster: device select + fullscreen + exit */}
            <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <select
                value={viewport}
                onChange={(e) => setViewport(e.target.value)}
                style={{
                  height: 32, padding: '0 8px', borderRadius: 9,
                  border: `1px solid ${BORDER}`, background: WHITE, color: TEXT2,
                  fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
                title="Device"
              >
                <option value="desktop">Desktop</option>
                <option value="tablet">Tablet</option>
                <option value="phone">Phone</option>
              </select>
              <button
                type="button"
                className="wpc-tool-btn"
                onClick={() => {
                  const iframe = iframeRef.current;
                  if (iframe && iframe.requestFullscreen) iframe.requestFullscreen().catch(() => {});
                }}
                title="Fullscreen"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9"/>
                  <polyline points="9 21 3 21 3 15"/>
                  <line x1="21" y1="3" x2="14" y2="10"/>
                  <line x1="3" y1="21" x2="10" y2="14"/>
                </svg>
              </button>
              <button
                type="button"
                onClick={openInNewTab}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  height: 32, padding: '0 14px', borderRadius: 9,
                  border: '1px solid rgba(255,255,255,0.3)',
                  background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                  color: '#fff', fontFamily: FONT, fontSize: 12, fontWeight: 700,
                  cursor: 'pointer',
                }}
                title="Open in a new tab"
              >
                Open
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Body: iframe + (optional) properties panel */}
          <div style={{ flex: 1, display: 'flex', minHeight: 0, background: '#0F172A' }}>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: viewport === 'desktop' ? 'stretch' : 'center', padding: viewport === 'desktop' ? 0 : 20, overflow: 'auto' }}>
              <iframe
                key={iframeKey}
                ref={iframeRef}
                title="Website expanded preview"
                srcDoc={expandedHtml}
                sandbox="allow-scripts allow-same-origin allow-forms"
                style={{
                  width: VIEWPORTS[viewport].width,
                  maxWidth: '100%',
                  height: viewport === 'desktop' ? '100%' : (viewport === 'tablet' ? 1100 : 780),
                  border: 'none',
                  background: '#fff',
                  borderRadius: viewport === 'desktop' ? 0 : 18,
                  boxShadow: viewport === 'desktop' ? 'none' : '0 24px 60px -20px rgba(0,0,0,0.5)',
                  display: 'block',
                }}
              />
            </div>

            {editMode && (
              <aside style={{
                width: 320, flexShrink: 0,
                background: 'rgba(255,255,255,0.96)',
                borderLeft: `1px solid ${BORDER}`,
                padding: 16, overflowY: 'auto',
                fontFamily: FONT,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: BLUE_DEEP, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Properties</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, margin: '4px 0 14px' }}>
                  {selected ? `<${selected.tag}>` : 'Select an element'}
                </h3>
                {!selected && (
                  <p style={{ fontSize: 13, color: TEXT2, lineHeight: 1.55 }}>
                    Click any element in the preview to edit its text, color, font, or background.
                  </p>
                )}
                {selected && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ fontSize: 11, color: TEXT3, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {selected.path}
                    </div>
                    <LabeledTextarea
                      label="Text"
                      value={selected.text || ''}
                      onChange={(v) => updateSelected({ text: v })}
                    />
                    <LabeledColor
                      label="Text color"
                      value={selected.color}
                      onChange={(v) => updateSelected({ color: v })}
                    />
                    <LabeledColor
                      label="Background"
                      value={selected.background}
                      onChange={(v) => updateSelected({ background: v })}
                    />
                    <LabeledInput
                      label="Font size"
                      value={selected.fontSize || ''}
                      onChange={(v) => updateSelected({ fontSize: v })}
                      placeholder="e.g. 18px"
                    />
                    <LabeledInput
                      label="Font weight"
                      value={selected.fontWeight || ''}
                      onChange={(v) => updateSelected({ fontWeight: v })}
                      placeholder="e.g. 600"
                    />
                  </div>
                )}
              </aside>
            )}
          </div>
        </div>
      ), document.body)}
    </>
  );
}

function fieldLabelStyle() {
  return { fontSize: 11, fontWeight: 700, color: TEXT2, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block' };
}
function fieldInputStyle() {
  return {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 10px', borderRadius: 8,
    border: `1px solid ${BORDER}`, background: WHITE,
    fontFamily: FONT, fontSize: 13, color: TEXT,
  };
}

function LabeledInput({ label, value, onChange, placeholder }) {
  return (
    <label>
      <span style={fieldLabelStyle()}>{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={fieldInputStyle()}
      />
    </label>
  );
}

function LabeledTextarea({ label, value, onChange }) {
  return (
    <label>
      <span style={fieldLabelStyle()}>{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        style={{ ...fieldInputStyle(), resize: 'vertical', lineHeight: 1.4 }}
      />
    </label>
  );
}

function LabeledColor({ label, value, onChange }) {
  // Convert rgb()/rgba() to hex for the picker if we can; otherwise fall
  // back to a text input so users can paste any CSS color value.
  const hex = rgbToHex(value);
  return (
    <label>
      <span style={fieldLabelStyle()}>{label}</span>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="color"
          value={hex || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 40, height: 36, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 2, background: WHITE, cursor: 'pointer' }}
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...fieldInputStyle(), flex: 1 }}
          placeholder="rgb(...) or #hex"
        />
      </div>
    </label>
  );
}

function rgbToHex(input) {
  if (!input || typeof input !== 'string') return null;
  const m = input.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return /^#[0-9a-f]{6}$/i.test(input) ? input : null;
  const r = (+m[1]).toString(16).padStart(2, '0');
  const g = (+m[2]).toString(16).padStart(2, '0');
  const b = (+m[3]).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}
